import React, { useEffect, useMemo, useState } from 'react';
import { testingService } from '../../services/api';

const VERDICT_BADGE_CLASSES_DARK = {
  improved: 'bg-emerald-900/60 text-emerald-200 border-emerald-700/60',
  degraded: 'bg-red-900/60 text-red-200 border-red-700/60',
  changed: 'bg-amber-900/60 text-amber-200 border-amber-700/60',
  consistent: 'bg-slate-700/60 text-slate-200 border-slate-500/60',
};

const VERDICT_BADGE_CLASSES_LIGHT = {
  improved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  degraded: 'bg-red-50 text-red-700 border-red-200',
  changed: 'bg-amber-50 text-amber-800 border-amber-200',
  consistent: 'bg-gray-100 text-gray-700 border-gray-200',
};

const VERDICT_LABEL = {
  improved: 'Improved',
  degraded: 'Degraded',
  changed: 'Changed',
  consistent: 'Consistent',
};

const VerdictBadge = ({ verdict, theme = 'light' }) => {
  const key = String(verdict || '').toLowerCase();
  const palette = theme === 'dark' ? VERDICT_BADGE_CLASSES_DARK : VERDICT_BADGE_CLASSES_LIGHT;
  const cls = palette[key] || palette.consistent;
  return (
    <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full border ${cls}`}>
      {VERDICT_LABEL[key] || (verdict ? String(verdict) : 'Consistent')}
    </span>
  );
};

const formatReportLabel = (report) => {
  const startedAt = report?.started_at ? new Date(report.started_at) : null;
  const startedLabel = startedAt && !Number.isNaN(startedAt.getTime())
    ? startedAt.toLocaleString()
    : '—';
  const status = report?.status ? String(report.status).replace(/_/g, ' ') : 'unknown';
  return `${report?.plan_name || 'Plan'} • ${startedLabel} • ${status}`;
};

/**
 * AI-powered before/after comparison of two saved run reports for a plan.
 *
 * Self-contained and reusable: fetches the plan's saved run reports, lets the
 * user pick a "before" (A) and an "after" (B), and renders the structured
 * comparison (overall verdict + per-case verdicts) returned by the backend.
 *
 * @param {Object} props
 * @param {string} props.clinicId
 * @param {string|null} props.planId - The selected plan; comparison is scoped to it.
 * @param {boolean} props.isRunningPlan - Disables actions while a plan run is active.
 * @param {'light'|'dark'} [props.theme='light'] - Visual theme for the sidebar vs dark modals.
 */
const RunReportComparePanel = ({ clinicId, planId, isRunningPlan, theme = 'light' }) => {
  const isLight = theme === 'light';
  const styles = {
    muted: isLight ? 'text-gray-500' : 'text-gray-400',
    label: isLight ? 'text-gray-600' : 'text-gray-400',
    select: isLight
      ? 'w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800'
      : 'w-full bg-[#202C33] text-white text-xs rounded px-2 py-1.5 border border-[#2A3942]',
    compareBtn: isLight
      ? 'px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
      : 'px-3 py-1.5 text-xs rounded bg-[#1d4f8c] text-blue-100 hover:bg-[#2465b2] disabled:opacity-50 disabled:cursor-not-allowed',
    warn: isLight ? 'text-[11px] text-amber-700' : 'text-[11px] text-amber-300',
    error: isLight ? 'text-xs text-red-700' : 'text-xs text-red-300',
    card: isLight
      ? 'p-2 rounded border border-gray-200 bg-gray-50 space-y-1'
      : 'p-2 rounded border border-[#2A3942] bg-[#202C33] space-y-1',
    cardTitle: isLight ? 'text-xs font-medium text-gray-900 truncate' : 'text-xs font-medium text-white truncate',
    cardBody: isLight ? 'text-xs text-gray-700 whitespace-pre-wrap' : 'text-xs text-gray-200 whitespace-pre-wrap',
    overallBody: isLight ? 'text-xs text-gray-800 whitespace-pre-wrap' : 'text-xs text-gray-200 whitespace-pre-wrap',
  };
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportIdA, setReportIdA] = useState('');
  const [reportIdB, setReportIdB] = useState('');
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setComparison(null);
    setError(null);
    setReportIdA('');
    setReportIdB('');
    if (!clinicId || !planId) {
      setReports([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingReports(true);
    testingService.listRunReports(clinicId, { planId, limit: 50 })
      .then((res) => {
        if (cancelled) return;
        if (res.success && Array.isArray(res.reports)) {
          setReports(res.reports);
        } else {
          setReports([]);
          if (res.error) setError(res.error);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load run reports.');
      })
      .finally(() => {
        if (!cancelled) setLoadingReports(false);
      });
    return () => { cancelled = true; };
  }, [clinicId, planId]);

  const canCompare = reportIdA && reportIdB && reportIdA !== reportIdB && !comparing && !isRunningPlan;

  const handleCompare = async () => {
    if (!canCompare) return;
    setComparing(true);
    setError(null);
    setComparison(null);
    try {
      const res = await testingService.compareRunReports(reportIdA, reportIdB);
      if (res.success && res.comparison) {
        setComparison(res.comparison);
      } else {
        setError(res.error || 'Failed to compare reports.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to compare reports.');
    } finally {
      setComparing(false);
    }
  };

  const comparisonCases = useMemo(
    () => (Array.isArray(comparison?.cases) ? comparison.cases : []),
    [comparison]
  );

  if (!planId) {
    return (
      <div className={`text-xs ${styles.muted}`}>
        Select a plan to compare its run reports.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {loadingReports ? (
        <div className={`text-xs ${styles.muted}`}>Loading run reports…</div>
      ) : reports.length < 2 ? (
        <div className={`text-xs ${styles.muted}`}>
          Need at least two saved run reports for this plan to compare ({reports.length} found).
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2">
            <div className="space-y-1">
              <label className={`block text-[11px] ${styles.label}`}>Before (A)</label>
              <select
                value={reportIdA}
                onChange={(e) => setReportIdA(e.target.value)}
                disabled={isRunningPlan}
                className={styles.select}
              >
                <option value="">Select a report…</option>
                {reports.map((report) => (
                  <option key={report.id} value={report.id}>{formatReportLabel(report)}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className={`block text-[11px] ${styles.label}`}>After (B)</label>
              <select
                value={reportIdB}
                onChange={(e) => setReportIdB(e.target.value)}
                disabled={isRunningPlan}
                className={styles.select}
              >
                <option value="">Select a report…</option>
                {reports.map((report) => (
                  <option key={report.id} value={report.id}>{formatReportLabel(report)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleCompare}
              disabled={!canCompare}
              className={styles.compareBtn}
            >
              {comparing ? 'Comparing…' : 'Compare with AI'}
            </button>
            {reportIdA && reportIdB && reportIdA === reportIdB && (
              <span className={styles.warn}>Pick two different reports.</span>
            )}
          </div>
        </>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {comparison && (
        <div className="space-y-2 mt-1">
          <div className={styles.card}>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] uppercase tracking-wide ${styles.label}`}>Overall</span>
              <VerdictBadge verdict={comparison.overall_verdict} theme={theme} />
            </div>
            <div className={styles.overallBody}>
              {comparison.overall_summary || 'No overall summary provided.'}
            </div>
          </div>

          {comparisonCases.length === 0 ? (
            <div className={`text-xs ${styles.muted}`}>No per-case comparison returned.</div>
          ) : (
            <div className="space-y-2 max-h-[36vh] overflow-y-auto pr-1">
              {comparisonCases.map((caseItem, idx) => (
                <div
                  key={`${caseItem?.name || 'case'}-${idx}`}
                  className={styles.card}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={styles.cardTitle}>
                      {caseItem?.name || `Case ${idx + 1}`}
                    </span>
                    <VerdictBadge verdict={caseItem?.verdict} theme={theme} />
                    <span className={`text-[11px] ${styles.muted}`}>
                      {String(caseItem?.before_status || '—')} → {String(caseItem?.after_status || '—')}
                    </span>
                  </div>
                  {caseItem?.summary && (
                    <div className={styles.cardBody}>{caseItem.summary}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RunReportComparePanel;
